/**
 * ReportToolbar — shared toolbar for every report tab.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ ↻ Refresh    💾 Save snapshot     ⬇ Download PDF              │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Save snapshot calls POST .../reports/snapshots with the current
 * report's `data`. Download PDF triggers a server-side fpdf2 render and
 * streams it to a fresh tab.
 */
import React, { useState } from 'react';
import { Save, Download, RefreshCw, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReportToolbar({
  waveId, reportId, currentData, onRefresh, onSnapshotSaved,
}) {
  const [savingSnap, setSavingSnap] = useState(false);
  const [snapDone, setSnapDone] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh?.(); } finally { setRefreshing(false); }
  };

  const handleSaveSnapshot = async () => {
    if (!currentData) return;
    setSavingSnap(true);
    try {
      const defaultLabel = window.prompt(
        'Snapshot label (helps you compare reports over time):',
        `${reportId} · ${new Date().toLocaleDateString()}`
      );
      if (!defaultLabel) {
        setSavingSnap(false);
        return;
      }
      const res = await fetch(`${API}/api/brand/waves/${waveId}/reports/snapshots`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: reportId, label: defaultLabel, data: currentData }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      const json = await res.json();
      setSnapDone(true);
      setTimeout(() => setSnapDone(false), 2400);
      onSnapshotSaved?.(json);
    } catch (e) {
      console.error('Snapshot save failed', e);
      window.alert('Snapshot save failed — please try again.');
    } finally {
      setSavingSnap(false);
    }
  };

  const pdfUrl = `${API}/api/brand/waves/${waveId}/reports/${reportId}/export.pdf`;

  return (
    <div className="flex items-center gap-2" data-testid={`report-toolbar-${reportId}`}>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        data-testid={`report-toolbar-${reportId}-refresh`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-black uppercase tracking-[0.06em] border hover:brightness-110 disabled:opacity-60"
        style={{ background: 'transparent', color: '#cbd5e1', borderColor: '#2E3240' }}
      >
        <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh
      </button>
      <button
        type="button"
        onClick={handleSaveSnapshot}
        disabled={savingSnap || !currentData}
        data-testid={`report-toolbar-${reportId}-save`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-black uppercase tracking-[0.06em] border hover:brightness-110 disabled:opacity-60"
        style={{
          background: snapDone ? 'rgba(74,222,128,0.10)' : 'rgba(167,139,250,0.10)',
          color: snapDone ? '#4ade80' : '#A78BFA',
          borderColor: snapDone ? 'rgba(74,222,128,0.40)' : 'rgba(167,139,250,0.40)',
        }}
      >
        {snapDone ? <Check className="h-3 w-3" /> : <Save className="h-3 w-3" />}
        {snapDone ? 'Saved' : (savingSnap ? 'Saving…' : 'Save snapshot')}
      </button>
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`report-toolbar-${reportId}-pdf`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-black uppercase tracking-[0.06em] hover:brightness-110"
        style={{ background: '#00D4FF', color: '#062314' }}
      >
        <Download className="h-3 w-3" />
        Download PDF
      </a>
    </div>
  );
}
